<template>
  <div style="display: inline !important">
    <div ref="base" :class="{ inline: defaultSlotIsInline }">
      <slot />
    </div>
    <Teleport v-if="global" to="body">
      <div
        ref="popper"
        :class="
          animate && displayChanged
            ? display
              ? 'fade-in in'
              : animationDone
                ? 'out'
                : 'fade-out out'
            : display
              ? 'in'
              : 'out'
        "
        class="base-popper"
      >
        <slot name="popper" />

        <div v-if="arrow" ref="arrow" :class="arrowClass" class="base-popper-arrow" />
      </div>
    </Teleport>
    <div
      v-else
      ref="popper"
      :class="
        animate && displayChanged
          ? display
            ? 'fade-in in'
            : animationDone
              ? 'out'
              : 'fade-out out'
          : display
            ? 'in'
            : 'out'
      "
      class="base-popper"
    >
      <slot name="popper" />

      <div v-if="arrow" ref="arrow" :class="arrowClass" class="base-popper-arrow" />
    </div>
  </div>
</template>

<script>
import { createPopper } from '@popperjs/core'
import { validatePopperPlacement } from '@/utils/propValidation'
/**
 * Default slot is used for the trigger content. #popper slot is used for the popper content
 */
export default {
  name: 'BasePopper',
  props: {
    display: {
      type: Boolean,
      required: true,
    },
    arrow: {
      type: Boolean,
      // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
      default: true,
    },
    arrowClass: {
      type: [String, Object, Array],
      default: '',
    },
    defaultSlotIsInline: {
      type: Boolean,
      default: false,
    },
    animate: {
      type: Boolean,
      default: false,
    },
    placement: {
      type: String,
      default: 'bottom',
      validator(value) {
        return validatePopperPlacement(value)
      },
    },
    modifiers: {
      type: [Array],
      default: () => [],
    },
    strategy: {
      type: String,
      default: 'absolute', // Other option is fixed
    },
    // Instead of appearing below, absolutely-position over button
    positionOverTrigger: {
      type: Boolean,
      default: false,
      required: false,
    },
    global: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      resizeWatcher: null,
      // At start if animation is set to true, we will see the popper fade out when it gets initialized.
      // This then ensure that animations are only allowed to fire after the display property has changed.
      displayChanged: false,
      animationDone: true,
    }
  },
  watch: {
    // Added as external factors that might not be communicated to popper can cause it to move and break the popover
    // to be mis-positioned. This is a relatively cheap operation, so should fix the issue.
    display(val) {
      if (val) {
        this.update()
      }
    },
  },
  created() {
    // Simply watch for display to change once, then stop watching it
    this.unwatch = this.$watch('display', () => {
      this.displayChanged = true
      this.animationDone = false
      setTimeout(() => {
        this.animationDone = true
      }, 300)
      this.unwatch()
    })
  },
  mounted() {
    if (typeof ResizeObserver !== 'undefined') {
      // Should be part of most browsers but check for it here
      this.resizeWatcher = new ResizeObserver(this.update)
    }
    this.setup()
  },
  methods: {
    update() {
      if (this.popper) {
        this.popper.update()
      }
    },
    setup() {
      const element = this.$refs.base.children[0]
      // Add one pixel to account for borders on triggers
      const offsetY = this.positionOverTrigger ? (element.clientHeight + 1) * -1 : 8
      const offsetX = this.positionOverTrigger ? 1 : 0

      // So some cases if our element is not a valid html element, we can't bind ourselves to the instances,
      // and therefore we will simply stop executing any code
      if (this.resizeWatcher && element instanceof HTMLElement) {
        this.resizeWatcher.observe(element)
        this.resizeWatcher.observe(this.$refs.popper)
      }
      this.popper = createPopper(element, this.$refs.popper, {
        placement: this.placement,
        strategy: this.strategy,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [offsetX, offsetY],
            },
          },
          {
            // Due to the nesting of arrows we need to enable them via references otherwise errors will occur
            name: 'arrow',
            enabled: this.arrow,
            options: {
              element: this.$refs.arrow,
            },
          },
          ...this.modifiers,
        ],
      })
    },
  },
}
</script>

<style scoped lang="scss">
.in {
  opacity: 1;
  visibility: visible;
}

.out {
  opacity: 0;
  visibility: hidden;
}

.fade-in {
  animation: fadeIn ease 0.3s;
}
@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.fade-out {
  animation: fadeOut ease 0.3s;
}
@keyframes fadeOut {
  0% {
    opacity: 1;
    visibility: visible;
  }
  100% {
    visibility: hidden;
    opacity: 0;
  }
}
</style>
